import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Label } from '../../../../components/ui/label';
import { Badge } from '../../../../components/ui/badge';
import { TabsContent } from '../../../../components/ui/tabs';
import { InvestorPicker } from '../../../../components/InvestorPicker';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import {
  Banknote,
  Trash2,
  CheckCircle,
  AlertTriangle,
  Link,
} from 'lucide-react';
import {
  API_URL,
  getTransactionConfig,
  formatCurrency,
  formatDate,
} from '../utils/formatters';
import { SortableHeader, sortItems } from '../utils/sorting';

export function BankStatementTabPanel({ ctx }) {
  const {
    statementInvestor, setStatementInvestor,
    investors,
    fetchStatement,
    statementLoading,
    statement,
    selectedStmt, setSelectedStmt,
    headers,
    toggleSelectAll, toggleSelect,
    sortStmt, setSortStmt, toggleSort,
    setAllocateDialog,
  } = ctx;

  return (
    <TabsContent value="statement" className="space-y-4" data-testid="statement-tab">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Banknote className="h-4 w-4" />Investor Bank Statement</CardTitle>
          <CardDescription>View running balance and allocate unallocated transactions to loans.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1 max-w-xs">
              <Label className="text-xs text-gray-500">Select Investor</Label>
              <InvestorPicker
                value={statementInvestor}
                onChange={(v) => setStatementInvestor(v === 'all' ? '' : v)}
                investors={investors.map(i => ({
                  id: i.id,
                  name: `${i.first_name || ''} ${i.last_name || ''}`.trim() || i.entity_name || i.email || i.id,
                  rp_code: i.risk_participant_code || '',
                  email: i.email || '',
                }))}
                includeAll={false}
                placeholder="Select investor"
                className="w-full"
                testId="statement-investor-select"
              />
            </div>
            <Button onClick={fetchStatement} disabled={!statementInvestor || statementLoading} data-testid="load-statement-btn">
              {statementLoading ? 'Loading...' : 'Load Statement'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {statement && (
        <>
          {/* Reconciliation Banner */}
          {statement.reconciliation && (
            <div className={`p-4 rounded-lg border ${statement.reconciliation.status === 'ok' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-center gap-2">
                {statement.reconciliation.status === 'ok' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                <span className={`font-medium text-sm ${statement.reconciliation.status === 'ok' ? 'text-green-800' : 'text-amber-800'}`}>
                  {statement.reconciliation.status === 'ok' ? 'Investec balance matches transaction history' : 'Balance mismatch — requires rectification'}
                </span>
              </div>
              {statement.reconciliation.status !== 'ok' && (
                <div className="mt-2 text-sm text-amber-700 ml-6 grid grid-cols-3 gap-2">
                  <div>Investec balance: <strong>{formatCurrency(statement.reconciliation.investec_balance)}</strong></div>
                  <div>Transaction total: <strong>{formatCurrency(statement.reconciliation.transaction_balance)}</strong></div>
                  <div>Difference: <strong className="text-red-600">{formatCurrency(statement.reconciliation.difference)}</strong></div>
                </div>
              )}
            </div>
          )}

          {/* Statement header */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">{statement.investor_name}</CardTitle>
                  <p className="text-sm text-gray-500">{statement.investor_email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Closing Balance</p>
                  <p className={`text-2xl font-bold ${statement.closing_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(statement.closing_balance)}</p>
                </div>
              </div>
              {statement.unallocated_count > 0 && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />{statement.unallocated_count} transaction(s) not allocated to a loan
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {selectedStmt.size > 0 && (
                <div className="flex items-center gap-3 p-3 bg-amber-50 border-b border-amber-200">
                  <span className="text-sm font-medium">{selectedStmt.size} selected</span>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-600 border-red-200" onClick={async () => {
                    if (!window.confirm(`Delete ${selectedStmt.size} transactions?`)) return;
                    for (const id of selectedStmt) await axios.delete(`${API_URL}/api/admin/investor-transactions/${id}`, { headers }).catch(() => {});
                    toast.success(`Deleted ${selectedStmt.size}`); setSelectedStmt(new Set()); fetchStatement();
                  }}><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setSelectedStmt(new Set())}>Clear</Button>
                </div>
              )}
              {(() => { const sortedStmtTxns = sortItems(statement.transactions, sortStmt); return (
              <Table>
                <TableHeader><TableRow className="bg-gray-50">
                  <TableHead className="w-8"><input type="checkbox" checked={statement.transactions.length > 0 && selectedStmt.size === statement.transactions.length} onChange={() => toggleSelectAll(setSelectedStmt, statement.transactions.map(t => t.id))} className="rounded" /></TableHead>
                  <SortableHeader label="Date" sortKey="transaction_date" sortState={sortStmt} onSort={toggleSort(setSortStmt)} />
                  <SortableHeader label="Type" sortKey="transaction_type" sortState={sortStmt} onSort={toggleSort(setSortStmt)} />
                  <SortableHeader label="Reference" sortKey="reference" sortState={sortStmt} onSort={toggleSort(setSortStmt)} />
                  <SortableHeader label="Loan ID" sortKey="loan_id" sortState={sortStmt} onSort={toggleSort(setSortStmt)} />
                  <SortableHeader label="Amount" sortKey="signed_amount" sortState={sortStmt} onSort={toggleSort(setSortStmt)} className="text-right" />
                  <SortableHeader label="Balance" sortKey="running_balance" sortState={sortStmt} onSort={toggleSort(setSortStmt)} className="text-right" />
                  <TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {sortedStmtTxns.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400">No transactions found</TableCell></TableRow>
                  ) : sortedStmtTxns.map(txn => {
                    const config = getTransactionConfig(txn.transaction_type);
                    const isUnallocated = !txn.loan_id && ['investment', 'deal_purchase', 'deal_repayment'].includes(txn.transaction_type);
                    return (
                      <TableRow key={txn.id} className={`text-sm ${selectedStmt.has(txn.id) ? 'bg-amber-50' : isUnallocated ? 'bg-amber-50/40' : ''}`}>
                        <TableCell><input type="checkbox" checked={selectedStmt.has(txn.id)} onChange={() => toggleSelect(setSelectedStmt)(txn.id)} className="rounded" /></TableCell>
                        <TableCell>{formatDate(txn.transaction_date)}</TableCell>
                        <TableCell><Badge className={config.color + ' text-xs'}>{config.label}</Badge></TableCell>
                        <TableCell className="text-gray-600">{txn.reference || '-'}</TableCell>
                        <TableCell>{txn.loan_id ? <span className="font-mono text-xs text-blue-600">{txn.loan_id}</span> : <span className="text-gray-300 text-xs">—</span>}</TableCell>
                        <TableCell className={`text-right font-medium ${txn.signed_amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>{txn.signed_amount >= 0 ? '+' : ''}{formatCurrency(txn.signed_amount)}</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(txn.running_balance)}</TableCell>
                        <TableCell>
                          {isUnallocated && (
                            <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-amber-700 border-amber-300" onClick={() => setAllocateDialog({ open: true, txn, loanId: '' })} data-testid={`allocate-${txn.id}`}>
                              <Link className="h-3 w-3 mr-1" />Allocate
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              ); })()}
            </CardContent>
          </Card>
        </>
      )}
    </TabsContent>
  );
}
